<template>
  <div id="login-wrapper">
    <div class="bg-ball-cyan"></div>
    <div class="bg-ball-blue"></div>

    <div class="login-general-card">
      <div class="login-logo-container">
        <button
          class="login-logo-btn"
          type="button"
          @click="handlePageChange('landing')"
        >
          <picture>
            <source srcset="../assets/img/logo-MEC.webp" type="image/webp" />
            <source srcset="../assets/img/logo-MEC.png" type="image/png" />
            <img
              src="../assets/img/logo-MEC.png"
              alt="Logo MEC"
              width="76"
              height="42"
            />
          </picture>
        </button>
      </div>

      <div class="login-titles-container">
        <p class="login-subtitle">Bienvenido a nuestra</p>
        <h2 class="login-title txt-grad-color-2">Inteligencia Artificial</h2>
      </div>

      <button
        class="login-image-container"
        type="button"
        @click="handlePageChange('landing')"
      >
        <picture>
          <source srcset="../assets/img/logo-MEC.webp" type="image/webp" />
          <source srcset="../assets/img/logo-MEC.png" type="image/png" />
          <img
            src="../assets/img/logo-MEC.png"
            alt="Logo MEC"
            width="140"
            height="77"
          />
        </picture>
      </button>

      <form class="login-form" @submit.prevent="handleLogin">
        <label class="login-label" for="login-usuario"
          >Usuario/Correo Electrónico:
          <input
            id="login-usuario"
            class="login-input"
            :class="{ 'login-invalid-input': errors.usuario }"
            type="text"
            v-model="usuario"
            ref="loginUsuario"
          />

          <p v-if="errors.usuario" class="login-invalid-feedback txt-bold">
            {{ errors.usuario }}
          </p>
        </label>

        <label class="login-label" for="login-password"
          >Contraseña:
          <input
            id="login-password"
            class="login-input"
            :class="{ 'login-invalid-input': errors.password }"
            :type="showPassword ? 'text' : 'password'"
            :autocomplete="showPassword ? 'none' : 'new-password'"
            v-model="password"
            ref="loginPassword"
          />

          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="currentColor"
            class="bi bi-eye-fill login-eye-icon"
            viewBox="0 0 16 16"
            @click="showPassword = !showPassword"
          >
            <defs>
              <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop
                  offset="0%"
                  style="stop-color: rgb(0, 246, 187); stop-opacity: 1"
                />
                <stop
                  offset="100%"
                  style="stop-color: rgb(29, 143, 206); stop-opacity: 1"
                />
              </linearGradient>
            </defs>
            <path
              fill="url(#grad2)"
              d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z"
            />
            <path
              fill="url(#grad2)"
              d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z"
            />
          </svg>
        </label>

        <p v-if="errors.password" class="login-invalid-feedback txt-bold">
          {{ errors.password }}
        </p>

        <p v-if="generalError" class="login-invalid-feedback-general txt-bold">
          {{ generalError }}
        </p>

        <button
          class="login-forgot-password-btn"
          type="button"
          @click="handlePageChange('recover')"
        >
          ¿Olvidaste tu contraseña?
        </button>

        <button
          class="login-submit-btn"
          :data="loading ? 'Ingresando' : 'Ingresar'"
          :disabled="loading"
        >
          <div class="lds-ring" v-if="loading">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </button>
      </form>

      <div class="login-register-container">
        <span class="login-register-txt">¿No tienes cuenta?</span>
        <button
          class="login-register-btn txt-bold"
          type="button"
          @click="handlePageChange('register')"
        >
          Crea una
        </button>
      </div>

      <div class="login-footer-container">
        <p class="login-footer-txt">Desarrollado por:</p>
        <a href="https://www.aprocom.com.co/" target="_blank">
          <img
            src="../assets/img/logo-aprocom.webp"
            alt="Logo Aprocom"
            title="APROCOM SIGLO XXI S.A.S"
            width="30px"
            height="30px"
          />
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import MEC_Funcionarios_api from "@/modules/MEC_Funcionarios_api";

export default {
  data() {
    return {
      errors: {},
      focusError: [],
      generalError: "",
      isLogged: false,
      usuario: "",
      password: "",
      showPassword: false,
      loading: false,
    };
  },

  methods: {
    async handleLogin() {
      const apiFuncionarios = new MEC_Funcionarios_api();
      this.loading = true;

      if (this.validateLoginForm()) {
        this.generalError = "";

        await apiFuncionarios
          .login(this.usuario, this.password)
          .then((result) => {
            if (result.message) {
              this.loading = false;
              this.generalError = result.message;
            } else if (result.token) {
              this.loading = false;
              this.isLogged = true;
              this.$emit("getSessionInfo");
              this.handlePageChange("workspace");
            } else {
              this.loading = false;
              this.generalError = "Ha ocurrido un error";
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }
    },

    validateLoginForm() {
      this.errors = {};
      this.focusError = [];

      this.usuario = this.usuario.trim();
      this.password = this.password.trim();

      if (!this.usuario) {
        this.errors.usuario = "Debes ingresar un usuario o correo elecrónico";
        this.focusError.push("loginUsuario");
      }

      if (!this.password) {
        this.errors.password = "Debes ingresar tu contraseña";
        this.focusError.push("loginPassword");
      }

      if (this.focusError.length > 0) {
        this.focusInput(this.focusError[0]);
      }

      // setTimeout(() => {
      //   this.errors = {};
      //   this.focusError = [];
      // }, 5000);

      return Object.keys(this.errors).length === 0;
    },

    focusInput(inputRef) {
      this.$nextTick(() => {
        this.$refs[inputRef].focus();
      });
    },

    handlePageChange(page) {
      this.$emit("pageChange", { page, lastPage: "login" });
    },
  },
};
</script>

<style>
/* ---------------------------------------------- Contenedor general Login */
#login-wrapper {
  height: 100%;
  width: 100%;
  overflow-y: auto;
  background-color: var(--bg-color);
  /* background-image: url("../assets/img/MEC-bg-image-1.png");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center; */
}

.bg-ball-blue {
  position: absolute;
  border-radius: 100%;
  width: 50vw;
  height: 70vh;
  right: 0;
  top: 30vh;
  background-image: var(--grad-color-azul);
  filter: blur(158.5px);
}

.bg-ball-cyan {
  position: absolute;
  border-radius: 100%;
  width: 50vw;
  height: 70vh;
  left: 0px;
  top: 0px;
  background-image: var(--grad-color-2);
  filter: blur(158.5px);
  animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }

  100% {
    transform: scale(1);
  }
}

@media screen and (min-width: 2500px) {
  .bg-ball-blue {
    filter: blur(300px);
  }
  .bg-ball-cyan {
    filter: blur(300px);
  }
}

/* ---------------------------------------------- Contenedor interno Login */
.login-general-card {
  margin: 40px auto;
  width: 90%;
  max-width: 1248px;
  min-height: 400px;
  padding: 30px 60px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 5;
}

.login-logo-container {
  width: 100%;
  display: flex;
}

.login-logo-btn {
  all: unset;
  cursor: pointer;
  transition: transform 0.3s ease-in-out;
}

.login-logo-btn:focus {
  transform: scale(1.1);
}

.login-titles-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.login-subtitle {
  margin: 0;
  font-size: 20px;
  color: var(--white);
  text-align: center;
}

.login-title {
  margin: -10px 0 0 0;
  font-family: var(--special-font);
  text-align: center;
  font-size: 40px;
}

.login-image-container {
  all: unset;
  margin: 4rem auto 2rem auto;
  cursor: pointer;
  transition: transform 0.3s ease-in-out;
}

.login-image-container:focus {
  transform: scale(1.1);
}

@media screen and (max-width: 768px) {
  .login-general-card {
    padding: 80px 30px;
  }

  .login-logo-container {
    display: none;
  }

  .login-subtitle {
    font-size: 14px;
  }

  .login-title {
    font-size: 25px;
  }

  .login-image-container {
    margin: 5rem auto;
  }
}

/* ---------------------------------------------- Estilos formulario Login */

.login-form {
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  width: 90%;
  max-width: 300px;
  gap: 20px;
}

.login-label {
  width: 100%;
  color: var(--white);
  font-weight: 700;
  display: flex;
  flex-direction: column;
  gap: 5px;
  position: relative;
}

.login-input {
  outline: none;
  padding: 0 1rem;
  width: 100%;
  height: 48px;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: 15px;
  color: var(--white);
}

.login-input:focus {
  border: 1px solid var(--accent-color);
  box-shadow: 0 0 0.5rem 1px #49fffcbc;
}

.login-eye-icon {
  position: absolute;
  right: 5%;
  top: 58%;
  cursor: pointer;
}

.login-invalid-feedback {
  margin: 0;
  color: red;
  font-size: 12px;
  width: 100%;
  text-align: center;
}

.login-invalid-feedback-general {
  margin: 0;
  color: red;
  font-size: 16px;
  width: 100%;
  text-align: center;
}

.login-invalid-input {
  border: 1px solid red;
}

.login-invalid-input:focus {
  border: 1px solid red;
  box-shadow: 0 0 1rem 5px #ff5c5cbc;
}

.login-forgot-password-btn {
  all: unset;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  color: var(--white);
  text-decoration: underline;
  font-size: 14px;
  font-weight: 200;
}

.login-forgot-password-btn:focus {
  color: var(--accent-color);
}

.login-submit-btn {
  margin: 0 auto;
  outline: none;
  border: none;
  background-image: var(--grad-color-2);
  width: 130px;
  height: 48px;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
  color: var(--white);
  border-radius: 15px;
  position: relative;
}

.login-submit-btn:disabled {
  background-image: none;
  cursor: not-allowed;
}

.login-submit-btn::before {
  content: "";
  position: absolute;
  top: 2px;
  right: 2px;
  bottom: 2px;
  left: 2px;
  background-color: var(--input-bg);
  border-radius: 13px;
  transition: background 0.3s ease-in-out;
}

.login-submit-btn::after {
  content: attr(data);
  font-size: 16px;
  font-weight: 700;
  color: var(--white);
  z-index: 10;
}

.login-submit-btn:hover::before {
  background-color: transparent;
}

.login-submit-btn:focus {
  outline: 1px solid #fff;
  outline-offset: 2px;
}

.login-submit-btn:disabled::after {
  color: var(--input-border);
}

.login-submit-btn:disabled .lds-ring div {
  border: 4px solid var(--input-border);
  border-color: var(--input-border) transparent transparent transparent;
}

.login-register-container {
  margin: 40px auto;
  display: flex;
  width: 90%;
  max-width: 300px;
  gap: 5px;
  justify-content: center;
}

.login-footer-container {
  margin: 0 auto;
  width: 90%;
  max-width: 300px;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
}

.login-footer-txt {
  margin: 0;
  color: var(--white);
  font-size: 12px;
}

.login-register-txt {
  color: var(--white);
  font-weight: 200;
}

.login-register-btn {
  all: unset;
  cursor: pointer;
  color: var(--white);
  position: relative;
  transition: color 0.3s ease-in-out;
}

.login-register-btn::before {
  content: "";
  position: absolute;
  bottom: -1px;
  left: 0;
  height: 2px;
  background-color: var(--white);
  width: 0px;
  transition: width 0.1s linear;
}

.login-register-btn:hover {
  color: var(--hover-white);
}

.login-register-btn:focus::before {
  width: 100%;
}

@media screen and (max-width: 768px) {
  .login-label {
    font-size: 14px;
  }

  .login-forgot-password-btn {
    font-size: 12px;
  }

  .login-submit-btn::after {
    font-size: 14px;
  }

  .login-submit-btn {
    margin: 1rem auto;
  }

  .login-register-txt {
    font-size: 14px;
  }

  .login-register-btn {
    font-size: 14px;
  }
}
</style>
